import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Landmark,
  MapPin,
  MessageCircle,
  Phone,
  Play,
  Receipt,
  Search,
  ShieldCheck,
  Smartphone,
  Star,
  Users,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  OperateurLogo,
  type Operateur,
} from "@/components/payer/OperateurLogo";
import { getDioceses, searchEcoles } from "@/lib/api";

const showcaseEcoles = [
  "Notre-Dame de la Paix (Cocody)",
  "Sainte-Marie (Cocody)",
  "Saint-Jean-Baptiste de la Salle (Plateau)",
  "Sainte-Bernadette (Marcory)",
  "Notre-Dame des Apôtres (Adjamé)",
  "Saint-Augustin (Yopougon)",
  "Saint-Joseph (Treichville)",
  "Sacré-Cœur (Bingerville)",
  "Sainte-Thérèse (Abobo)",
  "Saint-Viateur (Riviera)",
  "Saint-Michel (Anyama)",
  "Notre-Dame de la Compassion (Port-Bouët)",
  "Saint-Paul (Koumassi)",
  "Sainte-Famille (Attécoubé)",
  "Saint-Pierre (Cocody)",
  "Notre-Dame du Rosaire (Yopougon)",
  "Petit Séminaire (Anyama)",
  "Sacré-Cœur des Jésuites (Cocody)",
];

// CARTE_BANCAIRE volontairement masquée pour le moment (paiement carte désactivé,
// cohérent avec la page de paiement). Réactiver en ré-ajoutant l'entrée.
const paymentMethods: { name: string; op: Operateur }[] = [
  { name: "Wave", op: "WAVE" },
  { name: "Orange Money", op: "ORANGE_MONEY" },
  { name: "MTN MoMo", op: "MTN_MOMO" },
  { name: "Moov Money", op: "MOOV_MONEY" },
  { name: "Djamo", op: "DJAMO" },
];

const steps = [
  {
    icon: Search,
    title: "1. Cherchez l'école",
    desc: "Sélectionnez l'établissement de votre enfant parmi les écoles catholiques de Côte d'Ivoire.",
    color: "text-blue-500",
    bg: "bg-blue-50",
    image: "/images/steps/step-1-search.webp",
    imageAlt: "Maman ivoirienne cherchant l'école de son enfant sur son téléphone",
  },
  {
    icon: Smartphone,
    title: "2. Identifiez l'enfant",
    desc: "Saisissez son matricule SEDEC, ou téléversez un document (bulletin, reçu, certificat).",
    color: "text-coral",
    bg: "bg-coral/15",
    image: "/images/steps/step-2-identify.webp",
    imageAlt: "Papa et son enfant en uniforme avec un bulletin scolaire",
  },
  {
    icon: CreditCard,
    title: "3. Payez facilement",
    // La carte bancaire est masquée (voir paymentMethods) : elle n'est plus annoncée.
    desc: "Wave, Orange Money, MTN MoMo, Moov Money ou Djamo : choisissez ce qui vous arrange.",
    color: "text-gold",
    bg: "bg-gold/20",
    image: "/images/steps/step-3-pay.webp",
    imageAlt: "Parent réglant les frais scolaires depuis son smartphone",
  },
  {
    icon: Receipt,
    title: "4. Recevez le reçu",
    desc: "Votre reçu officiel certifié SEDEC est disponible en quelques secondes.",
    color: "text-sage",
    bg: "bg-sage/30",
    image: "/images/steps/step-4-receipt.webp",
    imageAlt: "Reçu officiel CATHO SCHOOL téléchargé sur smartphone",
  },
];

const faqs = [
  {
    q: "Où trouver le matricule de mon enfant ?",
    a: "Le matricule de l'élève figure sur le bulletin de l'année précédente ou vous a été communiqué par l'école lors de la pré-inscription. En cas de doute, contactez l'école. Vous pouvez aussi téléverser un document scolaire, nous extrairons les infos automatiquement.",
  },
  {
    q: "Y a-t-il des frais supplémentaires si je paie en ligne ?",
    a: "Aucune commission supplémentaire de notre part. Les frais affichés sont exactement ceux demandés par l'école. Seuls les frais standards de votre opérateur mobile (Wave, Orange Money, etc.) peuvent s'appliquer selon votre forfait.",
  },
  {
    q: "Mon reçu a-t-il une valeur officielle auprès de l'école ?",
    a: "Oui. Le reçu PDF généré porte une empreinte cryptographique SHA-256 et a la même valeur légale qu'un reçu tamponné. L'économe et l'Économat Diocésain sont automatiquement notifiés : pas besoin de repasser à l'école.",
  },
  {
    q: "Quelles écoles sont couvertes ?",
    a: "Pour la rentrée 2026-2027, 132 écoles catholiques de l'Archidiocèse d'Abidjan sont référencées (maternelle, primaire, secondaire général et technique, institut supérieur). Les autres diocèses suivront progressivement.",
  },
];

export default function Landing() {
  const [search, setSearch] = useState("");

  const { data: searchResults } = useQuery({
    queryKey: ["ecoles", "search", { q: search, size: 6 }],
    queryFn: () => searchEcoles({ q: search, size: 6 }),
    enabled: search.length > 1,
  });

  const { data: dioceses } = useQuery({
    queryKey: ["dioceses"],
    queryFn: () => getDioceses().catch(() => [] as Awaited<ReturnType<typeof getDioceses>>),
  });

  const activeDioceses = dioceses?.filter((d) => d.actif) ?? [];
  const totalEcoles = activeDioceses.reduce((sum, d) => sum + (d.nbEcoles ?? 0), 0) || 132;

  return (
    <div className="relative">
      {/* 1. Hero — plein cadre sombre (maman + enfant en uniforme) */}
      <section className="relative w-full min-h-[88vh] flex items-center overflow-hidden bg-navy">
        <img
          src="/images/hero-mother-child.webp"
          alt="Maman accompagnant son enfant en uniforme à l'école"
          className="absolute inset-0 w-full h-full object-cover object-center"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        {/* Voiles dégradés navy — lisibilité du texte par-dessus la photo */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/60 to-navy/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-navy/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white font-semibold text-sm mb-6">
              <Star size={16} className="fill-gold text-gold" />
              <span>Rentrée 2026-2027 : la plateforme est ouverte</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold leading-[1.1] mb-6 text-white">
              Payez la scolarité <br />
              <span className="text-gold relative">
                sans faire la queue.
                <svg
                  className="absolute w-full h-3 -bottom-1 left-0 text-gold/50"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 5 Q 50 10 100 5"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="transparent"
                  />
                </svg>
              </span>
            </h1>

            <p className="text-lg md:text-xl text-white/85 mb-10 leading-relaxed font-medium">
              Scolarité, cantine, transport, uniforme, sorties, examens…
              Réglez tous les frais scolaires de vos enfants en quelques clics.
              La plateforme officielle des écoles catholiques de Côte d'Ivoire.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                href="/ecoles"
                className="bg-coral text-white hover:bg-[#d16b50] px-8 py-4 rounded-full font-bold text-lg transition-all hover:-translate-y-0.5 shadow-lg shadow-coral/40 flex items-center justify-center gap-2"
              >
                Trouver mon école <ChevronRight size={20} />
              </Link>
              <a
                href="#comment-ca-marche"
                className="bg-white/10 backdrop-blur-md text-white border border-white/30 px-8 py-4 rounded-full font-bold text-lg transition-all hover:-translate-y-0.5 hover:bg-white/20 flex items-center justify-center gap-2"
              >
                <Play size={18} className="fill-current" /> Comment ça marche
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-sm font-semibold text-white/90">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-gold">
                  <Landmark size={22} />
                </div>
                <div>
                  <span className="block text-xl font-display text-white leading-tight">
                    {totalEcoles}
                  </span>
                  <span className="text-xs font-medium text-white/70">
                    Écoles à travers la CI
                  </span>
                </div>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-coral">
                  <Users size={22} />
                </div>
                <div>
                  {/* Le vrai nombre, tiré de la liste affichée plus bas
                      (constat du 21/10/2026 : « 16 modes » pour 5 réels). */}
                  <span className="block text-xl font-display text-white leading-tight">
                    {paymentMethods.length} moyens
                  </span>
                  <span className="text-xs font-medium text-white/70">
                    De paiement en ligne
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Live search */}
      <section className="px-6 py-16 bg-white border-y border-orange-50">
        <div className="max-w-3xl mx-auto text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-navy mb-2">
            Trouvez l'école de votre enfant
          </h2>
          <p className="text-navy/60">
            Tapez les premières lettres du nom ou de la commune.
          </p>
        </div>
        <div className="max-w-3xl mx-auto">
          <div className="relative mb-6">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-navy/40 w-6 h-6" />
            <input
              type="text"
              placeholder="ex: Notre-Dame, Sainte-Marie, Cocody…"
              className="w-full bg-cream border-2 border-orange-50 focus:border-coral/50 rounded-full py-4 pl-16 pr-6 text-lg font-medium outline-none transition-colors"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {searchResults?.items && searchResults.items.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {searchResults.items.map((ecole) => (
                <Link
                  key={ecole.id}
                  href={`/ecoles/${ecole.id}`}
                  className="bg-cream rounded-2xl p-4 border border-orange-50 hover:border-coral/40 hover:shadow-sm transition-all block"
                >
                  <h4 className="font-bold text-navy">{ecole.denomination}</h4>
                  <p className="text-xs text-navy/60 mt-1">
                    {ecole.communeNom ?? "·"}
                    {ecole.typeEnseignement ? ` · ${ecole.typeEnseignement}` : ""}
                  </p>
                </Link>
              ))}
            </div>
          )}
          {search.length > 1 &&
            searchResults?.items &&
            searchResults.items.length === 0 && (
              <p className="text-center text-navy/60 text-sm">
                Aucune école trouvée. Essayez avec moins de lettres ou consultez
                la liste complète.
              </p>
            )}
          <div className="text-center mt-6">
            <Link
              href="/ecoles"
              className="inline-flex items-center gap-2 text-coral font-bold hover:underline"
            >
              Voir les {totalEcoles} écoles <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* 3. How it works */}
      <section id="comment-ca-marche" className="py-24 bg-cream relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-display font-bold text-navy mb-4">
              C'est simple comme bonjour.
            </h2>
            <p className="text-lg text-navy/60 max-w-2xl mx-auto">
              En 3 minutes chrono, la scolarité de votre enfant est réglée et
              vous recevez votre reçu officiel.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="relative rounded-3xl bg-white border border-orange-50 shadow-sm hover:shadow-xl transition-shadow group overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-cream">
                  <img
                    src={step.image}
                    alt={step.imageAlt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div
                    className={`absolute bottom-3 left-3 w-12 h-12 rounded-2xl ${step.bg} backdrop-blur-sm flex items-center justify-center shadow-md`}
                  >
                    <step.icon size={22} className={step.color} />
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-display font-bold text-navy mb-2">
                    {step.title}
                  </h3>
                  <p className="text-navy/70 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Payment methods */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-full h-[200px] bg-gold/10 -skew-y-3 -z-10" />
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-navy mb-4">
            Payez avec votre méthode préférée
          </h2>
          <p className="text-lg text-navy/60 mb-12">
            Mobile Money, virement, chèque, espèces caisse… plusieurs modes au
            total, tous sécurisés.
          </p>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {paymentMethods.map((m) => (
              <div
                key={m.op}
                className="flex items-center gap-3 bg-white text-navy border border-orange-100 px-6 py-4 rounded-2xl font-bold text-lg shadow-sm hover:scale-105 hover:shadow-md transition-all cursor-default"
              >
                <OperateurLogo op={m.op} size={36} />
                {m.name}
              </div>
            ))}
          </div>

          <div className="mt-12 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-cream border border-orange-100">
            <ShieldCheck className="text-green-500" />
            <span className="font-semibold text-navy">
              Paiements sécurisés et traçables
            </span>
          </div>
        </div>
      </section>

      {/* 5. Écoles list */}
      <section id="ecoles" className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div>
              <h2 className="text-3xl md:text-5xl font-display font-bold text-navy mb-4">
                {totalEcoles} écoles partenaires
              </h2>
              <p className="text-lg text-navy/60 max-w-2xl">
                L'ensemble du réseau diocésain national est connecté :
                maternelle, primaire, secondaire général et technique, institut
                supérieur.
              </p>
            </div>
            <Link
              href="/ecoles"
              className="bg-white text-navy border border-orange-100 px-6 py-3 rounded-full font-bold transition-all hover:-translate-y-0.5 hover:shadow-md flex items-center gap-2"
            >
              Voir la liste complète <MapPin size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {showcaseEcoles.map((nom, idx) => (
              <Link
                key={idx}
                href="/ecoles"
                className="flex items-center p-4 rounded-2xl bg-white hover:bg-white border border-transparent hover:border-gold/30 hover:shadow-sm transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center mr-4 group-hover:bg-cream">
                  <Landmark size={20} className="text-navy" />
                </div>
                <span className="font-semibold text-navy group-hover:text-coral transition-colors">
                  {nom}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Trust & security */}
      <section
        id="securite"
        className="py-24 bg-navy text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 text-white">
              Tranquillité d'esprit absolue.
            </h2>
            <p className="text-white/80 text-lg mb-8 leading-relaxed">
              Vos données et votre argent sont protégés par les standards les
              plus stricts. Chaque reçu généré est authentifié et infalsifiable.
            </p>
            <ul className="space-y-4">
              {[
                "Supervisé par le SEDEC Diocésain et l'Économat",
                "Reçus certifiés par empreinte SHA-256 imprimée sur le PDF",
                "Conforme RGPD et Loi Ivoirienne 2013-450",
                "Transactions chiffrées de bout en bout : vos codes de paiement ne transitent jamais par nos serveurs",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="text-gold shrink-0 mt-1" size={20} />
                  <span className="text-white/90 font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white/10 p-8 md:p-12 rounded-[2.5rem] backdrop-blur-md border border-white/10">
            <div className="flex justify-center gap-8 items-center flex-wrap mb-10">
              <img
                src="/brand/logo-sedec.webp"
                alt="SEDEC"
                className="h-20"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <img
                src="/brand/logo-economat.webp"
                alt="Économat Diocésain"
                className="h-20"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div className="bg-white rounded-2xl p-6 text-navy text-center shadow-lg">
              <Receipt size={40} className="mx-auto text-coral mb-4" />
              <h4 className="font-display font-bold text-xl mb-2">
                Reçus 100% officiels
              </h4>
              <p className="text-sm text-navy/70">
                Les reçus numériques générés ici ont la même valeur légale que
                ceux imprimés à l'école, et sont infalsifiables.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-display font-bold text-navy mb-4">
              La parole aux parents
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-orange-50 relative">
              <div className="text-gold mb-4 flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={20} className="fill-current" />
                ))}
              </div>
              <p className="text-lg text-navy/80 italic mb-6 leading-relaxed">
                « Fini les matins à courir pour retirer du cash et faire la
                queue devant le bureau de l'économe. J'ai payé la scolarité de
                mes deux enfants depuis mon canapé hier soir. Un vrai
                soulagement ! »
              </p>
              <div className="flex items-center gap-4">
                <div className="relative w-12 h-12 rounded-full bg-coral text-white flex items-center justify-center font-bold text-xl shrink-0">
                  A
                  <img
                    src="/images/avatar-aminata.webp"
                    alt="Aminata K."
                    className="absolute inset-0 w-full h-full rounded-full object-cover border-2 border-coral/30"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <div>
                  <h4 className="font-bold text-navy">Aminata K.</h4>
                  <p className="text-sm text-navy/60">
                    Maman à Notre-Dame de la Paix
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-sm border border-orange-50 relative">
              <div className="text-gold mb-4 flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={20} className="fill-current" />
                ))}
              </div>
              <p className="text-lg text-navy/80 italic mb-6 leading-relaxed">
                « Ce qui me rassure, c'est le reçu PDF que je reçois
                immédiatement. Il y a le logo du diocèse et toutes les infos.
                C'est très carré. Le grand frère à Paris a même pu payer une
                partie ! »
              </p>
              <div className="flex items-center gap-4">
                <div className="relative w-12 h-12 rounded-full bg-sage text-white flex items-center justify-center font-bold text-xl shrink-0">
                  Y
                  <img
                    src="/images/avatar-yao.webp"
                    alt="Yao B."
                    className="absolute inset-0 w-full h-full rounded-full object-cover border-2 border-sage/40"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <div>
                  <h4 className="font-bold text-navy">Yao B.</h4>
                  <p className="text-sm text-navy/60">
                    Papa à Saint-Jean-Baptiste de la Salle
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. FAQ */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-navy mb-4">
              Des questions ?
            </h2>
            <p className="text-lg text-navy/60">On vous dit tout.</p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-orange-50 py-2"
              >
                <AccordionTrigger className="text-lg font-bold text-navy hover:text-coral text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-navy/70 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. Final CTA — mi-image / mi-texte */}
      <section className="py-20 px-6 bg-cream">
        <div className="max-w-6xl mx-auto bg-coral rounded-[3rem] overflow-hidden relative shadow-2xl shadow-coral/30 grid lg:grid-cols-2 items-stretch">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-gold/30 rounded-full blur-3xl" />
          <div className="p-10 md:p-16 relative z-10 flex flex-col justify-center">
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-6">
              Prêt pour une rentrée sereine ?
            </h2>
            <p className="text-white/90 text-lg md:text-xl mb-10">
              Créez votre compte parent en 2 minutes et payez la scolarité, la
              cantine, les sorties… sans bouger de chez vous.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/auth/register"
                className="bg-white text-coral px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-lg text-center"
              >
                Créer mon compte parent
              </Link>
              <Link
                href="/auth/login"
                className="bg-white/10 text-white border border-white/30 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-colors text-center"
              >
                J'ai déjà un compte
              </Link>
            </div>
          </div>
          <div className="relative min-h-[300px] lg:min-h-full">
            <img
              src="/images/cta-family.webp"
              alt="Famille ivoirienne heureuse réglant la scolarité en ligne"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-coral/60 via-transparent to-transparent lg:block hidden" />
          </div>
        </div>
      </section>

      {/* 10. Contact strip */}
      <section className="py-12 px-6 bg-navy text-white/80">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8 text-sm">
          <div className="flex items-center gap-3">
            <Phone size={18} className="text-gold shrink-0" />
            <span>Support parents : +225 00 00 00 00 00</span>
          </div>
          <div className="flex items-center gap-3">
            <MessageCircle size={18} className="text-gold shrink-0" />
            <span>support@cathoplus.ci</span>
          </div>
          <div className="flex items-start gap-3">
            <MapPin size={18} className="text-gold shrink-0 mt-0.5" />
            <span>Côte d'Ivoire</span>
          </div>
        </div>
      </section>
    </div>
  );
}
